import type { QueryKey } from '@tanstack/react-query'
import createFetchClient from 'openapi-fetch'
import createClient, { type MethodResponse } from 'openapi-react-query'
import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers'
import type { paths } from 'user-api/src/generated/openApi/schema'

const isEmptyQueryValue = (value: unknown) =>
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value)) ||
    (Array.isArray(value) && value.length === 0)

/**
 * Sorts params and drops empty ones so the same logical request always produces
 * the same url. Vercel's CDN keys on the url, so unsorted or empty-valued params
 * fragment the cache. Arrays stay as repeated keys (`?a=1&a=2`), which is what
 * the api's `toStringArray` preprocessors expect.
 */
const stableQuerySerializer = (query: Record<string, unknown>) =>
    Object.entries(query)
        .filter(([, value]) => !isEmptyQueryValue(value))
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .flatMap(([key, value]) =>
            Array.isArray(value)
                ? value
                      .filter((item) => !isEmptyQueryValue(item))
                      .map((item) => `${key}=${encodeURIComponent(String(item))}`)
                : [`${key}=${encodeURIComponent(String(value))}`]
        )
        .join('&')

export const fetchClient = createFetchClient<paths>({
    baseUrl: process.env.NEXT_PUBLIC_RESTAURANT_API_URL,
    headers: {
        // The browser forbids setting User-Agent, so only tag server-side traffic.
        ...(typeof window === 'undefined' ? { 'User-Agent': 'NeotasteWeb' } : {}),
    },
    querySerializer: stableQuerySerializer,
})

export const userApiClient = createClient(fetchClient)

/**
 * Response body for a spec path, as the client actually returns it.
 *
 * Deliberately not `paths[P][M]['responses'][200][...]`: openapi-fetch runs the
 * response through `Readable<>`, whose key filter is
 * `NonNullable<T[K]> extends $Write<any> ? never : K`. A property typed exactly
 * `null` narrows to `never`, `never extends` anything is true, and the key is
 * silently dropped. Deriving straight from `paths` would therefore produce
 * aliases that no query result is assignable to.
 */
export type ApiResponseBody<
    M extends HttpMethod,
    P extends PathsWithMethod<paths, M>,
> = MethodResponse<typeof userApiClient, M, P>

/**
 * Query options for an endpoint where a 404 means "no such thing", not a failure.
 * `userApiClient.queryOptions` always throws on a non-2xx, so these endpoints need
 * their own queryFn.
 *
 * `request` is a thunk rather than a `(path, init)` pair on purpose: keeping the
 * literal path at the call site avoids instantiating the union of every spec path,
 * which trips `TS2590: union type too complex to represent`.
 *
 * Pass `queryOptions(...).queryKey` so the key stays identical to what a plain
 * `userApiClient.useQuery` on the same path would produce.
 */
export function nullOn404<TData>(
    queryKey: QueryKey,
    request: (signal: AbortSignal) => Promise<{
        data?: TData
        error?: unknown
        response: Response
    }>
) {
    return {
        queryKey,
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
            const { data, error, response } = await request(signal)

            if (response.status === 404) return null
            if (error) throw error

            return data ?? null
        },
    }
}
